import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import {
  alpha,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import type { TalentSearchFeedback, TalentSearchFeedbackSuggestion } from '../../../../campaign-builder.types';

interface Props {
  open: boolean;
  feedback: TalentSearchFeedback;
  onAcceptTitle(suggestedTitle: string): void;
  onRetrySearch(): void;
  onClose(): void;
}

const TalentSearchFeedbackDialog: React.FC<Props> = ({
  open,
  feedback,
  onAcceptTitle,
  onRetrySearch,
  onClose,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [acceptedTypes, setAcceptedTypes] = useState<Set<string>>(new Set());

  const handleAcceptSuggestion = (suggestion: TalentSearchFeedbackSuggestion) => {
    if (suggestion.type === 'change_title' && suggestion.suggestedValue) {
      onAcceptTitle(suggestion.suggestedValue);
      setAcceptedTypes((prev) => new Set(prev).add('change_title'));
    }
  };

  const handleRetryAndClose = () => {
    onRetrySearch();
    onClose();
  };

  const hasAcceptedAny = acceptedTypes.size > 0;

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle className={classes.titleBar}>
        <Stack direction="row" alignItems="center" gap={1}>
          <LightbulbOutlinedIcon sx={{ color: '#FFF', fontSize: 20 }} />
          <Typography className={classes.titleText}>{t('talentSearchFeedbackTitle')}</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Typography className={classes.message}>{feedback.message}</Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          {feedback.suggestions.map((suggestion, idx) => (
            <SuggestionCard
              key={idx}
              suggestion={suggestion}
              accepted={acceptedTypes.has(suggestion.type)}
              onAccept={() => handleAcceptSuggestion(suggestion)}
              classes={classes}
              t={t}
            />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <PaiLink text={t('close')} size="sm" underline="always" action={onClose} />
        {hasAcceptedAny ? (
          <PaiButton
            text={t('talentSearchFeedbackRetry')}
            action={handleRetryAndClose}
            size="small"
            border="rounded"
          />
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

interface SuggestionCardProps {
  suggestion: TalentSearchFeedbackSuggestion;
  accepted: boolean;
  onAccept(): void;
  classes: ReturnType<typeof useStyles>['classes'];
  t: ReturnType<typeof useTranslation>['t'];
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({ suggestion, accepted, onAccept, classes, t }) => {
  const isActionable = suggestion.type === 'change_title' && !!suggestion.suggestedValue;

  return (
    <Box className={classes.card}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Chip
            label={t(`talentSearchFeedbackType_${suggestion.type}`)}
            size="small"
            className={classes.chip}
          />
          {accepted && <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 18 }} />}
        </Stack>

        <Typography className={classes.suggestionText}>{suggestion.text}</Typography>

        {isActionable && suggestion.currentValue && suggestion.suggestedValue && (
          <Box className={classes.changePreview}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography className={classes.changeLabel}>{suggestion.currentValue}</Typography>
              <Typography className={classes.arrow}>→</Typography>
              <Typography className={classes.changeSuggested}>{suggestion.suggestedValue}</Typography>
            </Stack>
          </Box>
        )}

        {isActionable && !accepted && (
          <Box sx={{ pt: 0.5 }}>
            <PaiButton
              text={t('talentSearchFeedbackAccept')}
              action={onAccept}
              size="extra-small"
              border="rounded"
              color="success"
            />
          </Box>
        )}

        {accepted && (
          <Typography className={classes.acceptedLabel}>{t('talentSearchFeedbackAccepted')}</Typography>
        )}
      </Stack>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  titleBar: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    padding: '14px 24px',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
  },
  content: {
    padding: '20px 24px !important',
  },
  message: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.primary,
    lineHeight: 1.6,
  },
  card: {
    padding: '14px 16px',
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  chip: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 600,
    height: 22,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
  suggestionText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  changePreview: {
    padding: '8px 12px',
    borderRadius: '8px',
    backgroundColor: alpha(theme.palette.warning.main, 0.08),
  },
  changeLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    textDecoration: 'line-through',
  },
  arrow: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  changeSuggested: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    color: theme.palette.success.dark,
  },
  acceptedLabel: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.success.main,
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default TalentSearchFeedbackDialog;
